import { createFileRoute, redirect } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import {
  Building2, Plus, RefreshCw, Pencil, Trash2, Check, Minus, Sparkles,
  Users2, TrendingUp, TrendingDown, Crown, Search, Download, Percent, Undo2,
  SlidersHorizontal, Ban, Mail,
} from 'lucide-react'
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import type { Feature } from '@shared/subscription'
import {
  useSubscriptionPlans,
  useCreatePlan,
  useUpdatePlan,
  useDeletePlan,
  useSubscriptionDashboard,
  useAllTenantSubscriptions,
  useSetSubscriptionDiscount,
  useMarkSubscriptionRefunded,
  useSetFeatureOverrides,
  useSubscriptionMrrTrend,
  useSubscriptionChurnTrend,
  useSubscriptionPlanDistribution,
} from '@/hooks/data/useSubscriptions'
import { useTenants, useCreateTenant, useDeactivateTenant, useInviteStoreOwner } from '@/hooks/data/useTenants'
import { subscriptionsApi } from '@/api/subscriptions'
import { decodeUser } from '@/api/jwt'
import { tokenStore } from '@/api/tokenStore'
import { ROUTES } from '@/config/routes'
import type { SubscriptionPlan, SubscriptionPlanInput, TenantSubscriptionRow, Tenant } from '@/types/domain'
import EditModal from '@/components/crud/EditModal'
import { useDeleteConfirm } from '@/components/crud/DeleteConfirm'
import { useAlertContext } from '@/providers/AlertProvider'
import { toast } from '@/ui/Toast'
import { userMessage } from '@/api/errors'

const PLAN_COLORS = ['#2563eb', '#16a34a', '#d97706', '#7c3aed', '#dc2626', '#0891b2']

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

const featureLabel: Record<Feature, string> = {
  'booking.online': 'Online booking',
  'scheduling.optimizer': 'Schedule optimizer',
  'workers.geofence': 'Worker geofencing',
  'billing.invoicing': 'Invoicing',
  'billing.accounting_export': 'Accounting export',
  'analytics.dashboards': 'Analytics dashboards',
  'campaigns.promos': 'Campaigns & promos',
  'ai.chat': 'AI chat assistant',
  'ai.auto_reschedule': 'AI auto-reschedule',
  'ai.dunning': 'AI dunning',
  'cms.multi_brand': 'Multi-brand CMS',
  'tenant.sso': 'SSO / SAML',
  'tenant.audit_log': 'Audit log',
  'marketplace.contractors': 'Contractor marketplace',
}

function StatCard({ icon: Icon, label, value, tone }: { icon: any; label: string; value: string; tone: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${tone}`}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        {label}
      </div>
      <div className="mt-2 text-xl font-bold text-gray-900 dark:text-white">{value}</div>
    </div>
  )
}

function PlatformStoresPage() {
  // ── Store registry (TenantsController) — the new part of Phase 2 ──
  const { data: tenants = [], isLoading: tenantsLoading, refetch: refetchTenants, isFetching: tenantsFetching } = useTenants()
  const createTenant = useCreateTenant()
  const deactivateTenant = useDeactivateTenant()
  const inviteOwner = useInviteStoreOwner()
  const { confirm } = useAlertContext()

  const [creatingStore, setCreatingStore] = useState(false)
  const [inviting, setInviting] = useState<Tenant | null>(null)

  const doDeactivate = (t: Tenant) => {
    confirm({
      title: 'Deactivate store',
      message: `Deactivate "${t.name}"? Its data is kept, but the storefront becomes unreachable and staff can no longer sign in.`,
      type: 'warning',
      confirmText: 'Deactivate',
      cancelText: 'Cancel',
      showCancel: true,
      onConfirm: async () => {
        try {
          await deactivateTenant.mutateAsync(t.id)
          toast.success(`${t.name} deactivated.`)
        } catch (err) {
          toast.error(userMessage(err))
        }
      },
    })
  }

  // ── Plan catalog + cross-tenant subscription/billing data (moved out of billing.tsx —
  // every hook/endpoint below is [RequirePlatformAdmin] server-side now, see abundant-stirring
  // -bee.md's Phase 1a; a store's own "admin" can no longer reach any of this, so it can't stay
  // on a page that role can navigate to). ──
  const { data: allPlans = [], isLoading: plansLoading, refetch: refetchPlans, isFetching: plansFetching } = useSubscriptionPlans(true)
  const { data: dashboard } = useSubscriptionDashboard()
  const { data: allTenantSubs = [], isLoading: tenantSubsLoading } = useAllTenantSubscriptions()
  const { data: mrrTrend = [] } = useSubscriptionMrrTrend()
  const { data: churnTrend = [] } = useSubscriptionChurnTrend()
  const { data: planDistribution = [] } = useSubscriptionPlanDistribution()

  const createPlan = useCreatePlan()
  const updatePlan = useUpdatePlan()
  const deletePlan = useDeletePlan()
  const setDiscount = useSetSubscriptionDiscount()
  const markRefunded = useMarkSubscriptionRefunded()
  const setFeatureOverrides = useSetFeatureOverrides()
  const confirmDeletePlan = useDeleteConfirm()

  const [creatingPlan, setCreatingPlan] = useState(false)
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null)

  const [tenantSearch, setTenantSearch] = useState('')
  const [tenantStatusFilter, setTenantStatusFilter] = useState('')
  const [tenantPlanFilter, setTenantPlanFilter] = useState('')
  const [discounting, setDiscounting] = useState<TenantSubscriptionRow | null>(null)
  const [refunding, setRefunding] = useState<TenantSubscriptionRow | null>(null)
  const [overridingFeatures, setOverridingFeatures] = useState<TenantSubscriptionRow | null>(null)

  const filteredTenantSubs = useMemo(() => {
    const q = tenantSearch.trim().toLowerCase()
    return allTenantSubs.filter((t) => {
      if (q && !t.tenantName.toLowerCase().includes(q)) return false
      if (tenantStatusFilter && t.status !== tenantStatusFilter) return false
      if (tenantPlanFilter && t.planName !== tenantPlanFilter) return false
      return true
    })
  }, [allTenantSubs, tenantSearch, tenantStatusFilter, tenantPlanFilter])

  const allFeatures = Array.from(new Set(allPlans.flatMap((p) => p.features))) as Feature[]

  const planFields = [
    { key: 'name', label: 'Plan Name', required: true },
    { key: 'description', label: 'Description', type: 'textarea' as const },
    { key: 'monthlyPrice', label: 'Monthly Price', type: 'number' as const, required: true },
    { key: 'annualPrice', label: 'Annual Price', type: 'number' as const, required: true, hint: 'Usually ~10 months worth to reward the annual commitment.' },
    {
      key: 'features', label: 'Features', type: 'multiselect' as const,
      options: (Object.keys(featureLabel) as Feature[]).map((f) => ({ value: f, label: featureLabel[f] })),
    },
    { key: 'maxUsers', label: 'Max Users', type: 'number' as const, required: true },
    { key: 'maxOrders', label: 'Max Orders', type: 'number' as const, required: true },
    { key: 'storageGb', label: 'Storage (GB)', type: 'number' as const, required: true },
    {
      key: 'isActive', label: 'Active', type: 'select' as const, required: true,
      options: [{ value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }],
    },
  ]

  const toPlanInput = (values: Record<string, any>, sortOrder: number): SubscriptionPlanInput => ({
    name: values.name,
    description: values.description ?? '',
    monthlyPrice: Number(values.monthlyPrice) || 0,
    annualPrice: Number(values.annualPrice) || 0,
    features: Array.isArray(values.features) ? values.features : [],
    maxUsers: Number(values.maxUsers) || 0,
    maxOrders: Number(values.maxOrders) || 0,
    storageGb: Number(values.storageGb) || 0,
    isActive: values.isActive === 'true',
    sortOrder,
  })

  return (
    <div className="p-2 md:p-4 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <Building2 className="w-6 h-6" /> Platform — Stores
        </h1>
        <p className="text-gray-600 dark:text-gray-400">Every store on this platform, its subscription, and plan catalog (platform admin only).</p>
      </div>

      {dashboard && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard icon={Users2} label="Active Subscriptions" value={String(dashboard.activeSubscriptions)} tone="bg-blue-50 text-blue-600 dark:bg-blue-900/30" />
          <StatCard icon={TrendingUp} label="MRR" value={`$${dashboard.mrr.toFixed(2)}`} tone="bg-green-50 text-green-600 dark:bg-green-900/30" />
          <StatCard icon={TrendingDown} label="Churn Rate (30d)" value={`${dashboard.churnRatePercent}%`} tone="bg-amber-50 text-amber-600 dark:bg-amber-900/30" />
          <StatCard icon={Crown} label="Popular Plan" value={dashboard.popularPlan ? `${dashboard.popularPlan} (${dashboard.popularPlanCount})` : '—'} tone="bg-purple-50 text-purple-600 dark:bg-purple-900/30" />
        </div>
      )}

      {/* Store registry */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-semibold text-gray-800 dark:text-white">Stores</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">The tenant registry itself (TenantsController) — create a store, then invite its owner.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => refetchTenants()} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
              <RefreshCw className={`w-4 h-4 ${tenantsFetching ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={() => setCreatingStore(true)} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm">
              <Plus className="w-4 h-4" /> New Store
            </button>
          </div>
        </div>
        {tenantsLoading ? (
          <p className="p-6 text-gray-500">Loading stores…</p>
        ) : tenants.length === 0 ? (
          <p className="p-6 text-gray-500 text-sm">No stores yet — create one above.</p>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Domain</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Created</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => (
                <tr key={t.id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-100">{t.name}</td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{t.domain}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${t.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {t.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{new Date(t.createdAt).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setInviting(t)}
                        className="p-1.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 text-blue-600"
                        title="Invite owner (safe to re-send)"
                      >
                        <Mail className="w-4 h-4" />
                      </button>
                      {t.isActive && (
                        <button
                          onClick={() => doDeactivate(t)}
                          className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"
                          title="Deactivate store"
                        >
                          <Ban className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Plan manager */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-gray-800 dark:text-white">Plan Manager</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Create, edit and deactivate the plan catalog every store subscribes from.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => refetchPlans()} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
              <RefreshCw className={`w-4 h-4 ${plansFetching ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={() => setCreatingPlan(true)} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm">
              <Plus className="w-4 h-4" /> New Plan
            </button>
          </div>
        </div>
        {plansLoading ? (
          <p className="p-6 text-gray-500">Loading plans…</p>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Monthly</th>
                <th className="px-4 py-2">Annual</th>
                <th className="px-4 py-2">Limits</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {allPlans.map((p) => (
                <tr key={p.id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-100">{p.name}</td>
                  <td className="px-4 py-3">${p.monthlyPrice.toFixed(2)}</td>
                  <td className="px-4 py-3">${p.annualPrice.toFixed(2)}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {p.maxUsers >= 2147483647 ? '∞' : p.maxUsers} users · {p.maxOrders >= 2147483647 ? '∞' : p.maxOrders} orders · {p.storageGb}GB
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {p.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setEditingPlan(p)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" title="Edit plan">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() =>
                          confirmDeletePlan(p.name, async () => {
                            try {
                              await deletePlan.mutateAsync(p.id)
                            } catch (err) {
                              toast.error(userMessage(err))
                            }
                          })
                        }
                        className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"
                        title="Delete plan"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* All tenants' subscriptions */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-gray-500" />
            <div>
              <h2 className="font-semibold text-gray-800 dark:text-white">Subscriptions</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Every store's subscription (SubscriptionsController.GET /tenants).</p>
            </div>
          </div>
          <button
            onClick={async () => {
              try {
                downloadBlob(await subscriptionsApi.exportTenantsCsv(), 'subscriptions.csv')
              } catch (err) {
                toast.error(userMessage(err))
              }
            }}
            className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>

        <div className="p-4 flex flex-wrap items-center gap-2 border-b border-gray-100 dark:border-gray-700">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
            <input
              value={tenantSearch}
              onChange={(e) => setTenantSearch(e.target.value)}
              placeholder="Search store name…"
              className="pl-8 pr-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm w-52"
            />
          </div>
          <select
            value={tenantStatusFilter}
            onChange={(e) => setTenantStatusFilter(e.target.value)}
            className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm"
          >
            <option value="">All statuses</option>
            {['Active', 'PastDue', 'Cancelled', 'Expired'].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            value={tenantPlanFilter}
            onChange={(e) => setTenantPlanFilter(e.target.value)}
            className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm"
          >
            <option value="">All plans</option>
            {Array.from(new Set(allTenantSubs.map((t) => t.planName).filter(Boolean))).map((p) => (
              <option key={p as string} value={p as string}>{p}</option>
            ))}
          </select>
          {(tenantSearch || tenantStatusFilter || tenantPlanFilter) && (
            <span className="text-xs text-gray-400">{filteredTenantSubs.length} of {allTenantSubs.length}</span>
          )}
        </div>

        {tenantSubsLoading ? (
          <p className="p-6 text-gray-500">Loading…</p>
        ) : filteredTenantSubs.length === 0 ? (
          <p className="p-6 text-gray-500 text-sm">No stores match these filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
                <tr>
                  <th className="px-4 py-2">Store</th>
                  <th className="px-4 py-2">Plan</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Cycle</th>
                  <th className="px-4 py-2">Value/mo</th>
                  <th className="px-4 py-2">Discount</th>
                  <th className="px-4 py-2">Refund</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTenantSubs.map((t) => (
                  <tr key={t.id} className="border-t border-gray-100 dark:border-gray-700">
                    <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-100">{t.tenantName}</td>
                    <td className="px-4 py-3">{t.planName ?? '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        t.status === 'Active' ? 'bg-green-100 text-green-700'
                          : t.status === 'Cancelled' ? 'bg-gray-100 text-gray-600'
                          : t.status === 'Expired' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>{t.status}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{t.billingCycle}</td>
                    <td className="px-4 py-3">${t.monthlyValue.toFixed(2)}</td>
                    <td className="px-4 py-3 text-xs">
                      {t.discountCode ? `${t.discountCode} (-${t.discountPercent}%)` : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {t.refundedAt ? (
                        <span title={t.refundReason ?? ''} className="text-amber-600">{new Date(t.refundedAt).toLocaleDateString()}</span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setDiscounting(t)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" title="Apply discount">
                          <Percent className="w-4 h-4" />
                        </button>
                        <button onClick={() => setRefunding(t)} className="p-1.5 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 text-amber-600" title="Mark refunded">
                          <Undo2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => setOverridingFeatures(t)} className="p-1.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 text-blue-600" title="Override features">
                          <SlidersHorizontal className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Analytics */}
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Subscription analytics</h2>
          <button
            onClick={async () => {
              try {
                downloadBlob(await subscriptionsApi.exportMrrCsv(), 'mrr-trend.csv')
              } catch (err) {
                toast.error(userMessage(err))
              }
            }}
            className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Download className="w-4 h-4" /> Export MRR CSV
          </button>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <h3 className="mb-1 text-sm font-semibold text-gray-800 dark:text-white">MRR — last 12 months</h3>
          <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
            Reconstructed from each subscription's start/cancel dates at its current plan &amp; discount — plan-change history isn't tracked, so a mid-window upgrade applies retroactively.
          </p>
          {mrrTrend.length === 0 ? (
            <p className="text-sm text-gray-500">No subscription history yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={mrrTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip formatter={(v) => `$${Number(v).toFixed(2)}`} />
                <Line type="monotone" name="MRR" dataKey="mrr" stroke="#2563eb" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
            <h3 className="mb-4 text-sm font-semibold text-gray-800 dark:text-white">Plan distribution</h3>
            {planDistribution.length === 0 ? (
              <p className="text-sm text-gray-500">No active subscriptions.</p>
            ) : (
              <div className="grid grid-cols-2 items-center gap-2">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={planDistribution} dataKey="subscriberCount" nameKey="planName" innerRadius={45} outerRadius={80} paddingAngle={2}>
                      {planDistribution.map((p, i) => <Cell key={p.planName} fill={PLAN_COLORS[i % PLAN_COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2 text-xs">
                  {planDistribution.map((p, i) => (
                    <div key={p.planName} className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: PLAN_COLORS[i % PLAN_COLORS.length] }} />
                      <span className="text-gray-600 dark:text-gray-300">{p.planName}</span>
                      <span className="ml-auto text-gray-400">{p.subscriberCount} · ${p.mrr.toFixed(0)}/mo</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
            <h3 className="mb-4 text-sm font-semibold text-gray-800 dark:text-white">Churn — last 12 months</h3>
            {churnTrend.length === 0 ? (
              <p className="text-sm text-gray-500">No cancellations yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={churnTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="churnedCount" name="Cancelled" fill="#dc2626" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Store modals */}
      <EditModal
        isOpen={creatingStore}
        onClose={() => setCreatingStore(false)}
        title="New Store"
        fields={[
          { key: 'name', label: 'Store name', required: true, placeholder: 'e.g. Sunrise Cleaning Co' },
          { key: 'domain', label: 'Domain', required: true, placeholder: 'e.g. sunrisecleaning.co.nz', hint: 'The storefront/admin host this store resolves on — lowercased automatically.' },
        ]}
        initialValues={{}}
        submitLabel="Create"
        onSubmit={async (values) => {
          const { id } = await createTenant.mutateAsync({
            name: String(values.name ?? '').trim(),
            domain: String(values.domain ?? '').trim(),
            isActive: true,
          })
          toast.success('Store created.')
          setInviting({ id, name: String(values.name), domain: String(values.domain), isActive: true, createdAt: '', updatedAt: '' })
        }}
      />

      <EditModal
        isOpen={!!inviting}
        onClose={() => setInviting(null)}
        title="Invite store owner"
        description={inviting ? `${inviting.name} — sends a 7-day signup link; safe to re-send if it expired or was lost.` : undefined}
        fields={[
          { key: 'email', label: 'Owner email', required: true, placeholder: 'owner@example.com' },
          { key: 'name', label: 'Owner name', required: true, placeholder: 'Jane Smith' },
        ]}
        initialValues={{}}
        submitLabel="Send invite"
        onSubmit={async (values) => {
          if (!inviting) return
          await inviteOwner.mutateAsync({ tenantId: inviting.id, email: String(values.email ?? '').trim(), name: String(values.name ?? '').trim() })
          toast.success('Invite sent.')
        }}
      />

      {/* Plan/subscription modals */}
      <EditModal
        isOpen={discounting !== null}
        onClose={() => setDiscounting(null)}
        title="Apply discount"
        description={discounting?.tenantName}
        fields={[
          { key: 'discountCode', label: 'Discount code', placeholder: 'e.g. LAUNCH20', hint: 'Clear this field and save to remove the discount.' },
          { key: 'discountPercent', label: 'Discount %', type: 'number' as const, placeholder: '0–100' },
        ]}
        initialValues={{ discountCode: discounting?.discountCode ?? '', discountPercent: discounting?.discountPercent ?? '' }}
        submitLabel="Save"
        onSubmit={async (values) => {
          if (!discounting) return
          const code = String(values.discountCode ?? '').trim()
          await setDiscount.mutateAsync({
            tenantId: discounting.tenantId,
            discountCode: code || null,
            discountPercent: code ? Number(values.discountPercent) || 0 : null,
          })
          toast.success(code ? 'Discount applied.' : 'Discount removed.')
        }}
      />

      <EditModal
        isOpen={refunding !== null}
        onClose={() => setRefunding(null)}
        title="Mark as refunded"
        description={refunding ? `${refunding.tenantName} — recorded manually, no charge is reversed (no payment processor is wired to subscriptions)` : undefined}
        fields={[{ key: 'reason', label: 'Reason', type: 'textarea' as const, required: true, placeholder: 'e.g. Refunded via bank transfer per support ticket #123' }]}
        initialValues={{ reason: '' }}
        submitLabel="Record refund"
        onSubmit={async (values) => {
          if (!refunding) return
          await markRefunded.mutateAsync({ tenantId: refunding.tenantId, reason: String(values.reason ?? '') })
          toast.success('Refund recorded.')
        }}
      />

      <EditModal
        isOpen={overridingFeatures !== null}
        onClose={() => setOverridingFeatures(null)}
        title="Override features"
        description={overridingFeatures ? `${overridingFeatures.tenantName} — grants these on top of ${overridingFeatures.planName ?? 'their plan'}'s own features; never removes what the plan already includes.` : undefined}
        fields={[
          {
            key: 'features', label: 'Extra features', type: 'multiselect' as const,
            options: (Object.keys(featureLabel) as Feature[]).map((f) => ({ value: f, label: featureLabel[f] })),
          },
        ]}
        initialValues={{ features: overridingFeatures?.overriddenFeatures ?? [] }}
        submitLabel="Save"
        onSubmit={async (values) => {
          if (!overridingFeatures) return
          await setFeatureOverrides.mutateAsync({
            tenantId: overridingFeatures.tenantId,
            features: Array.isArray(values.features) ? values.features : [],
          })
          toast.success('Feature overrides updated.')
        }}
      />

      <EditModal
        isOpen={creatingPlan}
        onClose={() => setCreatingPlan(false)}
        title="New Plan"
        fields={planFields}
        initialValues={{ isActive: 'true', features: [] }}
        submitLabel="Create"
        onSubmit={async (values) => {
          const nextOrder = allPlans.length ? Math.max(...allPlans.map((p) => p.sortOrder)) + 1 : 0
          await createPlan.mutateAsync(toPlanInput(values, nextOrder))
        }}
      />

      <EditModal
        isOpen={!!editingPlan}
        onClose={() => setEditingPlan(null)}
        title="Edit Plan"
        description={editingPlan?.name}
        fields={planFields}
        initialValues={
          editingPlan
            ? {
                name: editingPlan.name,
                description: editingPlan.description,
                monthlyPrice: editingPlan.monthlyPrice,
                annualPrice: editingPlan.annualPrice,
                features: editingPlan.features,
                maxUsers: editingPlan.maxUsers,
                maxOrders: editingPlan.maxOrders,
                storageGb: editingPlan.storageGb,
                isActive: editingPlan.isActive ? 'true' : 'false',
              }
            : {}
        }
        onSubmit={async (values) => {
          if (!editingPlan) return
          await updatePlan.mutateAsync({ id: editingPlan.id, body: toPlanInput(values, editingPlan.sortOrder) })
        }}
      />
    </div>
  )
}

export const Route = createFileRoute('/(admin)/platform/stores/')({
  // The (admin) parent layout's own beforeLoad only enforces role checks for paths registered
  // in shared-core's ROUTES config — this route is deliberately NOT registered there (see
  // AppSidebar.tsx's comment on why: ROUTES' roleMatches() treats bare "admin" as a universal
  // bypass, which a store's own admin must never get here), so without this guard ANY
  // authenticated user could load this page's shell (every real data call would still 403
  // server-side, but that's a broken-looking page, not the intended "you can't even see this
  // exists" UX for a platform-only surface).
  beforeLoad: () => {
    const tokens = tokenStore.get()
    const user = tokens ? decodeUser(tokens.accessToken) : null
    if (!user?.isPlatformAdmin) {
      throw redirect({ to: ROUTES.FORBIDDEN.path })
    }
  },
  component: PlatformStoresPage,
})
